import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';

interface SelectModeContextType {
  selectMode: boolean;
  setSelectMode: (mode: boolean) => void;
}

const SelectModeContext = createContext<SelectModeContextType | undefined>(
  undefined,
);

export const useSelectMode = () => {
  const context = useContext(SelectModeContext);
  if (context === undefined) {
    throw new Error('useSelectMode must be used within a SelectModeProvider');
  }
  return context;
};

interface SelectModeProviderProps {
  children: React.ReactNode;
}

export const SelectModeProvider: React.FC<SelectModeProviderProps> = ({
  children,
}) => {
  const { selectedFiles } = useSelector((state: RootState) => state.library);
  const selectedFilesCount = Object.keys(selectedFiles).length;
  const [selectMode, setSelectMode] = useState(selectedFilesCount > 0);
  const [previousSelectedCount, setPreviousSelectedCount] =
    useState(selectedFilesCount);

  // Auto-enter select mode when files are selected
  // Auto-exit select mode only when actively deselecting the last file
  useEffect(() => {
    if (selectedFilesCount > 0 && !selectMode) {
      // Auto-enter select mode when files are selected
      setSelectMode(true);
    } else if (
      selectedFilesCount === 0 &&
      previousSelectedCount > 0 &&
      selectMode
    ) {
      // Only auto-exit if we went from having selected files to having none (active deselection)
      setSelectMode(false);
    }

    // Update the previous count for next comparison
    setPreviousSelectedCount(selectedFilesCount);
  }, [selectedFilesCount, selectMode, previousSelectedCount]);

  return (
    <SelectModeContext.Provider value={{ selectMode, setSelectMode }}>
      {children}
    </SelectModeContext.Provider>
  );
};
